<template>
  <!-- backdrop 클릭으로 닫지 않음 (입력 중 misclick 으로 OTP 가 날아가는 문제 방지). X·닫기 버튼으로만 닫는다 -->
  <div class="modal-wrap-3" style="display: flex">
    <div class="modal-inner" @click.stop>
      <button type="button" class="close-modal" @click="closeModal">
        <img src="@/assets/images/icon/ico_close_w.svg" alt="닫기"/>
      </button>

      <div class="modal-content">
        <h4>이메일 인증</h4>
        <p v-if="purpose === 'find'" class="desc">
          가입 시 등록한 이메일로 인증해 주세요.
        </p>
        <p v-else class="desc">
          해외 거주 등으로 휴대폰 본인인증이 어려운 경우 이메일로 인증할 수 있습니다.<br/>
          가입에 사용할 이메일을 입력해 주세요.
        </p>

        <div class="field">
          <label for="email-verify-input">이메일</label>
          <div class="input-row">
            <input
                id="email-verify-input"
                ref="emailRef"
                v-model="email"
                type="email"
                class="form-control"
                placeholder="example@domain.com"
                :disabled="otpSent || isSending || isVerifying"
                @input="onEmailInput"
            />
            <button
                type="button"
                class="btn"
                :disabled="!canSendOtp"
                @click="sendOtp"
            >
              {{ otpSent ? '재발송' : '인증코드 받기' }}
            </button>
          </div>
          <p v-if="emailError" class="txt err">{{ emailError }}</p>
          <p v-if="otpSent && !verificationContextId" class="txt info">
            인증코드를 발송했습니다. 1시간 내에 입력해 주세요.
          </p>

          <!-- 가입 흐름에서 이미 가입된 이메일이면 아이디/비밀번호 찾기로 유도 -->
          <div v-if="alreadyRegistered" class="already-registered">
            <p class="txt err">이미 가입된 이메일입니다. 아이디 찾기 또는 비밀번호 찾기를 이용해 주세요.</p>
            <div class="find-actions">
              <button type="button" class="btn-find" @click="$emit('go-find-id')">아이디 찾기</button>
              <button type="button" class="btn-find" @click="$emit('go-find-pwd')">비밀번호 찾기</button>
            </div>
          </div>
        </div>

        <div v-if="otpSent" class="field">
          <label for="otp-input">인증코드 (6자리)</label>
          <div class="input-row">
            <input
                id="otp-input"
                ref="otpRef"
                v-model="otpInput"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="form-control"
                placeholder="6자리 숫자"
                :disabled="!!verificationContextId || isVerifying"
                @input="onOtpInput"
            />
            <button
                type="button"
                class="btn"
                :disabled="!canVerify"
                @click="verifyOtp"
            >
              인증
            </button>
          </div>
          <p v-if="otpError" class="txt err">{{ otpError }}</p>
          <p v-if="verificationContextId" class="txt success">
            이메일 인증이 완료되었습니다.
          </p>
        </div>

        <div class="button-box">
          <button type="button" class="btn-cancel" @click="closeModal">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, defineEmits, defineProps, ref} from "vue";
import {apiClient} from "@/services/auth-header";

// purpose: 'signup'(기본) — 이미 가입된 이메일이면 찾기로 유도 / 'find' — 아이디·비밀번호 찾기용(기가입 이메일 허용)
const props = defineProps({
  purpose: {type: String, default: 'signup'},
});

const emit = defineEmits(['close', 'verified', 'go-find-id', 'go-find-pwd']);

const alreadyRegistered = ref(false);

const email = ref("");
const otpInput = ref("");
const otpSent = ref(false);
const isSending = ref(false);
const isVerifying = ref(false);
const expiresAt = ref(null);
const verificationContextId = ref("");

const emailError = ref("");
const otpError = ref("");

const emailRef = ref(null);
const otpRef = ref(null);

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const isValidEmail = computed(() => emailRegex.test(email.value.trim()));
const canSendOtp = computed(
    () => isValidEmail.value && !isSending.value && !isVerifying.value && !verificationContextId.value
);
const canVerify = computed(
    () => otpSent.value && /^\d{6}$/.test(otpInput.value) && !isVerifying.value && !verificationContextId.value
);

const onOtpInput = () => {
  otpError.value = "";
  // 숫자만 허용
  otpInput.value = otpInput.value.replace(/\D/g, "").slice(0, 6);
};

const onEmailInput = () => {
  emailError.value = "";
  alreadyRegistered.value = false;
};

const sendOtp = async () => {
  if (!isValidEmail.value) {
    emailError.value = "유효한 이메일 주소를 입력해 주세요.";
    return;
  }

  emailError.value = "";
  otpError.value = "";
  alreadyRegistered.value = false;
  isSending.value = true;

  try {
    // 가입 흐름: 이미 가입된 이메일이면 OTP 발송 없이 찾기로 유도
    if (props.purpose === 'signup') {
      const dup = await apiClient.get("/v1/auth/is-duplicate-email", {
        params: {email: email.value.trim()},
      });
      if (dup.data) {
        alreadyRegistered.value = true;
        return;
      }
    }

    const response = await apiClient.post("/v1/auth/email/send-verification", {
      email: email.value.trim(),
      purpose: "SIGNUP",
    });
    expiresAt.value = response.data?.expiresAt || null;
    otpSent.value = true;
    // 인증코드 입력란으로 포커스
    setTimeout(() => {
      otpRef.value?.focus();
    }, 100);
  } catch (e) {
    const msg = e?.response?.data?.message;
    emailError.value = msg || "인증코드 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.";
  } finally {
    isSending.value = false;
  }
};

const verifyOtp = async () => {
  if (!/^\d{6}$/.test(otpInput.value)) {
    otpError.value = "6자리 숫자를 입력해 주세요.";
    return;
  }

  otpError.value = "";
  isVerifying.value = true;

  try {
    const response = await apiClient.post("/v1/auth/email/verify", {
      email: email.value.trim(),
      token: otpInput.value,
    });

    verificationContextId.value = response.data?.verificationContextId;
    if (!verificationContextId.value) {
      otpError.value = "인증에 실패했습니다. 잠시 후 다시 시도해 주세요.";
      return;
    }

    // 부모에 결과 전달 후 모달 종료
    emit('verified', {
      verificationContextId: verificationContextId.value,
      email: email.value.trim(),
    });
  } catch (e) {
    const code = e?.response?.data?.code;
    const msg = e?.response?.data?.message;

    if (code === "EMAIL_OTP_MISMATCH" || /불일치|mismatch/i.test(msg || "")) {
      otpError.value = "인증코드가 일치하지 않습니다.";
    } else if (code === "EMAIL_OTP_EXPIRED" || /만료|expired/i.test(msg || "")) {
      otpError.value = "인증코드가 만료되었습니다. 다시 발송해 주세요.";
    } else if (code === "EMAIL_OTP_ATTEMPTS_EXCEEDED" || /시도|attempt/i.test(msg || "")) {
      otpError.value = "시도 횟수를 초과했습니다. 인증코드를 다시 발송해 주세요.";
    } else {
      otpError.value = msg || "인증에 실패했습니다.";
    }
  } finally {
    isVerifying.value = false;
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped>
/* 공용 .modal-wrap-3 z-index 가 100 이라 로그인 찾기 모달(.modal-popup, z-index 99999) 뒤에
   깔려 안 보임 → 그 위로 올린다. */
.modal-wrap-3 {
  z-index: 100000;
}

/* 공용 modal-inner 가 좁고(max-width:343px) align-items:flex-end 라 content 가 폭을
   못 채워 입력란이 좁아짐. 폭을 넓히고 stretch 시켜 입력란이 모달 폭을 채우게 한다. */
.modal-wrap-3 .modal-inner {
  width: 460px;
  max-width: calc(100vw - 32px);
  align-items: stretch;
}

/* 닫기 X 는 우측 유지 (stretch 로 좌측 이동 방지) */
.modal-wrap-3 .modal-inner .close-modal {
  align-self: flex-end;
}

.modal-wrap-3 .modal-inner .modal-content {
  width: 100%;
  max-width: 460px;
  box-sizing: border-box;
  /* 공용 .modal-content 의 align-items:center 때문에 .field 가 내용 크기로 줄어 입력란이 좁아진다.
     stretch 로 덮어써 자식이 폭을 채우게 한다. */
  align-items: stretch;
  padding: 50px 30px;
  text-align: left;
}

/* .field 가 모달 폭을 채워야 input(flex:1)이 제대로 넓어진다 */
.modal-wrap-3 .modal-inner .modal-content .field {
  width: 100%;
}

.modal-content h4 {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 700;
}

.modal-content .desc {
  font-size: 13px;
  color: #555;
  margin-bottom: 24px;
  word-break: keep-all;
}

.field {
  margin-bottom: 20px;
}

.field label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

/* 입력란 + 버튼을 한 줄에 (input 이 남은 폭을 모두 차지) */
.input-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.input-row input.form-control {
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.input-row input.form-control:disabled {
  background-color: #f5f5f5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.input-row .btn {
  /* 인증코드받기/재발송/인증 버튼 폭을 동일하게 고정 (입력란 끝선도 정렬됨) */
  flex: 0 0 100px;
  width: 100px;
  white-space: nowrap;
  padding: 8px 0;
  text-align: center;
  border: 1px solid #151419;
  background-color: #151419;
  color: #fff;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.input-row .btn:disabled {
  background-color: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}

.txt {
  margin-top: 6px;
  font-size: 12px;
}

.txt.err {
  color: #e53935;
}

.txt.info {
  color: #555;
}

.txt.success {
  color: #2e7d32;
}

.already-registered {
  margin-top: 10px;
}

.already-registered .find-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.already-registered .btn-find {
  flex: 1;
  padding: 10px 0;
  border: 1px solid #151419;
  background-color: #fff;
  color: #151419;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.button-box {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.btn-cancel {
  padding: 8px 18px;
  background-color: #e0e0e0;
  color: #333;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}
</style>
